import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import {
  Item,
  ItemGroup,
  ItemSeparator,
  ItemHeader,
  ItemFooter,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
} from "../components/ui/item"
import { cn } from "../lib/utils"

/** Circular gauge SVG for displaying a percentage (matches shadcn usage-card pattern). */
function CircularGauge({ percentage }: { percentage: number }) {
  const p = Math.min(Math.max(percentage, 0), 100)
  const circumference = 2 * Math.PI * 42.5
  const strokePercent = (p / 100) * circumference
  return (
    <svg aria-hidden="true" fill="none" height="16" width="16" strokeWidth="2" viewBox="0 0 100 100" className="-rotate-90">
      <circle cx="50" cy="50" r="42.5" strokeWidth="12" strokeDashoffset="0" strokeLinecap="round" strokeLinejoin="round" className="opacity-20" stroke="currentColor" style={{ strokeDasharray: `${circumference} ${circumference}` }} />
      <circle cx="50" cy="50" r="42.5" strokeWidth="12" strokeDashoffset="0" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" className="transition-all duration-300" style={{ strokeDasharray: `${strokePercent} ${circumference}` }} />
    </svg>
  )
}

export function ItemRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  // item-group: wrap children in ItemGroup, rendering separators as ItemSeparator
  if (element.type === "item-group") {
    return (
      <ItemGroup
        className={cn(element.className)}
        data-testid={`item-group-${element.id}`}
      >
        {element.children?.map((child) => {
          if (child.type === "separator") {
            return <ItemSeparator key={child.id} className={child.className} />
          }
          if (child.type === "item-header") {
            return (
              <ItemHeader key={child.id} className={child.className}>
                {child.children?.map((c) => (
                  <ElementRenderer key={c.id} element={c} />
                ))}
              </ItemHeader>
            )
          }
          if (child.type === "item-footer") {
            return (
              <ItemFooter key={child.id} className={child.className}>
                {child.children?.map((c) => (
                  <ElementRenderer key={c.id} element={c} />
                ))}
              </ItemFooter>
            )
          }
          return <ElementRenderer key={child.id} element={child} />
        })}
      </ItemGroup>
    )
  }

  const variant = (element.variant ?? "default") as
    | "default"
    | "outline"
    | "muted"
  const size = ((element as unknown as Record<string, unknown>).size ?? "default") as
    | "default"
    | "sm"
    | "xs"

  // Compound mode: children are item-content, item-media, etc.
  // Slot renderers (registered in index.ts) handle each child type.
  const hasCompoundChildren = element.children?.some(
    (c) => c.type === "item-content" || c.type === "item-media" || c.type === "item-actions" || c.type === "item-header"
  )

  if (hasCompoundChildren) {
    return (
      <Item variant={variant} size={size} className={cn(element.className)} data-testid={`item-${element.id}`}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Item>
    )
  }

  // Legacy mode: title/description/icon as props
  const title = element.text
    ? String(resolveSymbol(element.text) ?? "")
    : ""
  const description = element.description
    ? String(resolveSymbol(element.description) ?? "")
    : ""
  const hasIcon = !!element.icon
  const percentage = (element as unknown as Record<string, unknown>).percentage as number | undefined
  const hasGauge = typeof percentage === "number"

  const isAction = (c: { type: string; slot?: string }) =>
    c.type === "button" || c.type === "icon-button" || c.type === "kbd" ||
    c.type === "dropdown-menu" ||
    (c as unknown as Record<string, unknown>).slot === "actions"
  const actionChildren = element.children?.filter(isAction)
  const contentChildren = element.children?.filter((c) => !isAction(c))

  return (
    <Item
      variant={variant}
      size={size}
      className={cn(element.className)}
      data-testid={`item-${element.id}`}
    >
      {hasGauge ? (
        <ItemMedia variant="icon" className="text-primary">
          <CircularGauge percentage={percentage!} />
        </ItemMedia>
      ) : hasIcon ? (
        <ItemMedia variant="icon">
          <ElementRenderer
            element={{
              id: `${element.id}-icon`,
              type: "icon",
              icon: element.icon,
            }}
          />
        </ItemMedia>
      ) : null}
      <ItemContent>
        {title && <ItemTitle>{title}</ItemTitle>}
        {description && <ItemDescription>{description}</ItemDescription>}
        {contentChildren?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </ItemContent>
      {actionChildren && actionChildren.length > 0 && (
        <ItemActions>
          {actionChildren.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </ItemActions>
      )}
    </Item>
  )
}
